import { Navigate } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';

/** The two taxonomy URL segments. `analysis` is the pre-rename (current) segment. */
export type AnalysisSegment = 'analysis' | 'reports';

/** The pages served under either segment. */
export type AnalysisPage = 'deliverability' | 'email-clients';

const PAGE_ROUTES = {
  analysis: {
    deliverability: '/workspaces/$workspaceId/analysis/deliverability',
    'email-clients': '/workspaces/$workspaceId/analysis/email-clients',
  },
  reports: {
    deliverability: '/workspaces/$workspaceId/reports/deliverability',
    'email-clients': '/workspaces/$workspaceId/reports/email-clients',
  },
} as const;

/** Which segment this account's URLs use — `reports` once the rename flag is on. */
export function canonicalAnalysisSegment(variant: 'current' | 'future'): AnalysisSegment {
  return variant === 'future' ? 'reports' : 'analysis';
}

export function analysisPageRoute(segment: AnalysisSegment, page: AnalysisPage) {
  return PAGE_ROUTES[segment][page];
}

/** True when `pathname` is this page under either taxonomy segment. */
export function isAnalysisPagePathname(pathname: string, page: AnalysisPage) {
  return pathname.includes(`/analysis/${page}`) || pathname.includes(`/reports/${page}`);
}

/**
 * Renders a Deliverability/Email clients page at whichever segment is canonical for the
 * account, and forwards the non-canonical segment to it (a flag-aware redirect).
 *
 * The redirect lives here (not in `beforeLoad`) on purpose, matching the Activity Logs
 * precedent: `beforeLoad` runs before `accountStore` is populated on a cold load, so a flag
 * read there races and disagrees with the rendered nav and tab labels. Reading the rename
 * stage from `useProductTaxonomy` — the same source the nav and page titles use — keeps the
 * URL vocabulary and the visible copy consistent.
 *
 * Sits above the plan guard and the `last` workspace resolver so a non-canonical URL is
 * corrected before either runs, and the `last` sentinel is forwarded untouched for the
 * canonical route to resolve.
 */
type AnalysisRouteViewProps = PropsWithChildren<{
  segment: AnalysisSegment;
  page: AnalysisPage;
  workspaceId: WorkspaceIdRouteParamInput;
}>;

export function AnalysisRouteView({ segment, page, workspaceId: workspaceIdParam, children }: AnalysisRouteViewProps) {
  const taxonomy = useProductTaxonomy();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const canonicalSegment = canonicalAnalysisSegment(taxonomy.variant);

  if (segment !== canonicalSegment) {
    // `search` carries the shared date range (start_time/end_time) across the redirect.
    return <Navigate to={analysisPageRoute(canonicalSegment, page)} params={{ workspaceId }} search replace />;
  }

  return children;
}
